// One answer to "is Apple Music connected?" for every surface. Two sources disagree
// on their own: the server knows whether a user token is stored (AppleMusicConnect's
// "Connected" badge), MusicKit knows whether THIS browser session is authorized
// (Redux player.authState, refreshed once when the player mounts). A stale Redux
// state made the page say "Connect" under a banner that said "Connected". This hook
// re-probes MusicKit on mount and when the tab regains focus, and reports connected
// when either source says so — playback still needs MusicKit, so callers that play
// keep using the provider's own errors.
import { useEffect, useState } from 'react';
import { useAppSelector } from '@/app/hooks';
import { getAppleMusicStatus } from '@/services/performanceService';
import { usePlayer } from '../player/usePlayer';

const CONNECTED = new Set(['connected_eligible', 'connected_no_subscription']);

export function useMusicConnected(): { connected: boolean; serverConnected: boolean; sessionConnected: boolean; refresh: () => void } {
  const { refreshAuth } = usePlayer();
  const authState = useAppSelector((s) => (s as any).player?.authState as string | undefined);
  const [serverConnected, setServerConnected] = useState(false);

  const refresh = () => {
    void refreshAuth().catch(() => undefined);
    getAppleMusicStatus().then((s) => setServerConnected(!!s.connected)).catch(() => undefined);
  };
  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionConnected = CONNECTED.has(authState || '');
  return { connected: sessionConnected || serverConnected, serverConnected, sessionConnected, refresh };
}
